import {
  Component,
  Inject,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ArticleService } from '../../../services/article.service';
import { CommentService } from '../../../services/comment.service';

interface FeedItem {
  id: number;
  type: 'article' | 'status' | 'comment' | 'topic';
  state: 'visible' | 'hidden';
  current_user_permissions?: {
    hide?: boolean;
  };
}

@Component({
  selector: 'ut-community-feed-item-hide',
  templateUrl: './community-feed-item-hide.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFeedItemHideComponent implements OnDestroy {
  @Input() item!: FeedItem;
  @Output() itemHidden = new EventEmitter<FeedItem>();
  @Output() stateChanged = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private isProcessing$ = new BehaviorSubject<boolean>(false);

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject('AlertService') private AlertService: any,
    @Inject('$uibModal') private $uibModal: any,
    private ArticleService: ArticleService,
    @Inject('StatusService') private StatusService: any,
    @Inject('DiscussionService') private DiscussionService: any,
    private CommentService: CommentService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isHidden(): boolean {
    return this.item?.state === 'hidden';
  }

  get ariaLabel(): string {
    return this.isHidden ? 'Show' : 'Hide';
  }

  get hasHidePermission(): boolean {
    return this.item?.current_user_permissions?.hide === true;
  }

  hidePopover(): void {
    if (this.isProcessing$.value) {
      return;
    }

    const modalInstance = this.$uibModal.open({
      animation: true,
      template: this.getModalTemplate(),
      controller: 'ModalCtrl',
      windowClass: 'community-feed-item-hide-modal',
      resolve: {
        data: () => ({
          item: this.item,
          hide: () => this.hideItem(),
          closeOverlay: () => modalInstance.close(),
        }),
      },
    });

    modalInstance.result.catch((resp: string) => {
      if (['cancel', 'backdrop click', 'escape key press'].indexOf(resp) === -1) {
        throw new Error(resp);
      }
    });
  }

  private hideItem(): void {
    if (this.isProcessing$.value) {
      return;
    }

    this.isProcessing$.next(true);

    let servicePromise: Promise<any>;

    switch (this.item.type) {
      case 'article':
        servicePromise = this.ArticleService.toggleHide(this.item);
        break;
      case 'status':
        servicePromise = this.StatusService.toggleHide(this.item);
        break;
      case 'comment':
        servicePromise = this.CommentService.toggleHide(this.item);
        break;
      case 'topic':
        servicePromise = this.DiscussionService.toggleHide(this.item);
        break;
      default:
        this.isProcessing$.next(false);
        return;
    }

    servicePromise
      .then((res) => this.successCallback(res))
      .catch(() => this.errorCallback())
      .finally(() => {
        this.isProcessing$.next(false);
        this.cdr.markForCheck();
      });
  }

  private successCallback(res: any): void {
    Object.assign(this.item, res.data);

    if (this.item.state === 'hidden') {
      this.AlertService.add('Successfully unpublished');
      this.itemHidden.emit(this.item);
    } else {
      this.AlertService.add('Successfully republished');
    }

    this.stateChanged.emit();
    this.cdr.markForCheck();
  }

  private errorCallback(): void {
    this.AlertService.add('There was an error', 'danger');
    this.cdr.markForCheck();
  }

  private getModalTemplate(): string {
    return `
      <div class="modal-header">
        <button type="button" class="close" ng-click="cancel()" aria-label="Close">
          <i class="icon-cancel" aria-hidden="true"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="feed-item-hide-message">
          {{item.state === 'hidden' ? 'Show this item?' : 'Hide this item?'}}
        </div>
        <button class="community-cta__button unfilled" autofocus ng-click="cancel()">Cancel</button>
        <button class="community-cta__button" ng-click="hide()" ng-mouseup="cancel()">{{item.state === 'hidden' ? 'Show' : 'Hide'}}</button>
      </div>
    `;
  }
}
